import { Controller } from "@hotwired/stimulus";
import { setNamedField, readNamedFieldValue, syncHouseholdIncomes } from "./utils/criteria_fields";
import { applyFiltersForm } from "./utils/filters_form";

// profile-wizard (Stimulus controller) — moteur du wizard « Nouveau profil » (#307)
//
// Pilote une séquence d'écrans rendue en **overlay au-dessus du simulateur
// vivant**. Le moteur est **générique** : il ne contient aucune logique métier
// propre à un écran. Il lit :
// - le **graphe de parcours** (ordre + branchement) sérialisé en JSON dans
//   `data-profile-wizard-config-value` (cf. `Wizards::ProfileWizard.front_config`) ;
// - les **écrans** rendus côté serveur (`data-profile-wizard-target="step"`,
//   un par écran, `data-step-id`) ;
// - les **champs** de chaque écran (`data-profile-wizard-target="field"`,
//   `data-field-name` = nom du champ canonique, `data-field-kind`).
//
// À chaque passage à l'écran suivant, les valeurs de l'écran courant sont
// poussées dans les champs **canoniques** du formulaire `#edit-profile-form`
// puis un évènement `input`/`change` est déclenché : les pipelines existants
// prennent le relais (persistance `update_criterias` via `edit-profile`,
// recalcul `api-sync`). Aucune logique de persistance dédiée, aucun
// `applyResponse` complet (pas de bascule d'onglet ni resync favoris en cours
// de parcours).
export default class extends Controller {
  static targets = ["step", "field", "progress", "stepLabel", "nextButton", "backButton"];

  static values = {
    config: Object,
    createUrl: String,
    sourceId: String,
    defaultLabel: String,
    // Écran de départ (ex. "mode" pour le bouton baguette magique) ; par défaut
    // le 1er écran du parcours ("name").
    startId: String,
    // Id du profil ACTIF lorsque le wizard édite un profil existant (baguette
    // magique) : court-circuite la création (`handleNameStep`).
    existingProfileId: String,
  };

  connect() {
    this.paths = (this.configValue && this.configValue.paths) || {};
    this.modeField = (this.configValue && this.configValue.modeField) || "profile[investment_mode]";
    this.modeMapping = (this.configValue && this.configValue.modeMapping) || {};
    this.pathKey = (this.configValue && this.configValue.defaultPath) || Object.keys(this.paths)[0];

    // Profil existant édité (baguette magique) : aucune création, on part de
    // l'écran de départ fourni (ex. "mode"), l'écran "name" est sauté.
    this.profileId =
      this.hasExistingProfileIdValue && this.existingProfileIdValue ? this.existingProfileIdValue : null;
    this.lastInvestTaxProgram = null;

    this.editForm = document.getElementById("edit-profile-form");

    this.startId =
      this.hasStartIdValue && this.startIdValue ? this.startIdValue : this.currentPath()[0];
    this.startIndex = this.indexInPath(this.startId);
    this.currentId = this.startId;

    this.prefillFromCanonical();
    this.showStep(this.currentId);
  }

  // ---------- Accès au graphe ----------
  currentPath() {
    return this.paths[this.pathKey] || [];
  }

  indexInPath(id) {
    return this.currentPath().indexOf(id);
  }

  isLastStep() {
    return this.indexInPath(this.currentId) === this.currentPath().length - 1;
  }

  // ---------- Navigation ----------
  next(event) {
    if (event) event.preventDefault();

    // Écran de création : crée le profil (une seule fois) avant d'avancer.
    if (this.currentId === "name") {
      this.handleNameStep().then(() => this.advance()).catch(() => {});
      return;
    }

    this.applyStep(this.currentStepElement());

    if (this.isLastStep()) {
      this.finish();
      return;
    }
    this.advance();
  }

  advance() {
    // Le branchement part de l'écran discriminant : sa valeur détermine le
    // parcours actif pour toute la suite.
    if (this.currentId === "mode") {
      const key = this.modeMapping[this.modeSelection()];
      if (key && this.paths[key]) this.pathKey = key;
    }

    const path = this.currentPath();
    const idx = path.indexOf(this.currentId);
    const nextId = path[idx + 1];
    if (!nextId) {
      this.finish();
      return;
    }
    this.currentId = nextId;
    this.showStep(this.currentId);
  }

  // « Retour » : sur le premier écran affiché (écran de départ du parcours),
  // ferme le wizard (même effet que « Saisie manuelle »). Au-delà, revient à
  // l'écran précédent.
  back(event) {
    if (event) event.preventDefault();
    const path = this.currentPath();
    const idx = path.indexOf(this.currentId);
    if (idx <= this.startIndex) {
      this.close();
      return;
    }
    this.currentId = path[idx - 1];
    this.showStep(this.currentId);
  }

  // « Saisie manuelle » : pousse les valeurs de l'écran courant puis ferme le
  // wizard sur le simulateur déjà rempli (sans perte de saisie).
  manualEntry(event) {
    if (event) event.preventDefault();
    if (this.currentId !== "name") this.applyStep(this.currentStepElement());
    this.close();
  }

  finish() {
    this.close();
  }

  // ---------- Affichage d'un écran ----------
  showStep(id) {
    this.stepTargets.forEach((step) => {
      step.classList.toggle("d-none", step.dataset.stepId !== id);
    });
    this.updateProgress();
    this.updateButtons();
    // Pas de focus automatique sur le premier champ (#398) : prendre le focus
    // bascule un champ `number-format` en mode édition (valeur brute) et masque
    // son unité (€ / mois, %, € / an…). On laisse donc l'utilisateur cliquer
    // lui-même pour que les unités restent visibles à l'arrivée sur l'écran.
  }

  currentStepElement() {
    return this.stepTargets.find((step) => step.dataset.stepId === this.currentId);
  }

  // Valeur sélectionnée sur l'écran discriminant (`mode`), utilisée pour
  // déterminer le parcours actif (branchement).
  modeSelection() {
    const step = this.stepTargets.find((s) => s.dataset.stepId === "mode");
    if (!step) return null;
    const field = this.fieldTargets.find((f) => step.contains(f) && f.dataset.fieldKind === "mode");
    return field ? this.selectedCardValue(field) : null;
  }

  updateProgress() {
    const path = this.currentPath();
    const position = path.indexOf(this.currentId) + 1;
    const total = path.length;
    if (this.hasProgressTarget && total > 0) this.renderDots(total, position);
    if (this.hasStepLabelTarget) {
      this.stepLabelTarget.textContent = `Étape ${position} / ${total}`;
    }
  }

  // Génère une rangée de pastilles (une par écran du parcours actif). Le nombre
  // de pastilles suit la longueur du parcours courant (qui change au
  // branchement). Étapes passées = pleine, courante = pill allongée, à venir =
  // grise.
  renderDots(total, position) {
    const container = this.progressTarget;
    container.innerHTML = "";
    for (let i = 1; i <= total; i += 1) {
      const dot = document.createElement("span");
      dot.className = "simulator-wizard-dot";
      if (i < position) dot.classList.add("is-done");
      else if (i === position) dot.classList.add("is-current");
      container.appendChild(dot);
    }
  }

  updateButtons() {
    // « Retour » est toujours visible : sur le premier écran il ferme le wizard
    // (cf. `back`).
    if (this.hasBackButtonTarget) {
      this.backButtonTarget.classList.remove("invisible");
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.textContent = this.isLastStep() ? "Terminer" : "Suivant";
    }
  }

  // ---------- Étape 1 : création du profil ----------
  handleNameStep() {
    const label = (this.profileNameValue() || "").trim();

    // Profil déjà créé (l'utilisateur est revenu sur l'écran 1) : on se contente
    // de propager le nom mis à jour via le champ canonique `criterias[label]`.
    if (this.profileId) {
      this.pushLabel(label);
      return Promise.resolve();
    }

    if (!this.hasCreateUrlValue) return Promise.resolve();

    const body = new URLSearchParams();
    body.set("label", label);
    if (this.hasSourceIdValue && this.sourceIdValue) body.set("source_id", this.sourceIdValue);

    return fetch(this.createUrlValue, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
        "X-CSRF-Token": this.csrfToken(),
      },
      credentials: "same-origin",
      body: body.toString(),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((json) => this.onProfileCreated(json))
      .catch(() => {});
  }

  onProfileCreated(json) {
    if (!json || !json.profile_id) return;
    this.profileId = json.profile_id;

    // Le profil créé est secondaire : on autorise désormais la persistance de
    // `criterias[label]` (le bloc « Nom du profil client » est marqué principal
    // au rendu initial car le profil actif l'était).
    this.markProfileSecondary();

    // Application immédiate des critères du profil créé au formulaire (#307,
    // étape 1 « appliquée en background »). C'est le seul moment où l'on applique
    // un jeu complet de critères ; les étapes suivantes ne poussent que leurs
    // propres champs.
    if (this.editForm && json.criterias) {
      Object.keys(json.criterias).forEach((key) =>
        setNamedField(this.editForm, `criterias[${key}]`, json.criterias[key])
      );
    }

    // Réinitialise aussi les filtres catalogue (#424) qui vivent dans
    // `#listing-filter-form` (orientation, étage, prix, tri, localisation,
    // nombre de pièces) et non dans `#edit-profile-form`. Sans ça, les valeurs
    // stales du profil précédent restent affichées — et peuvent être
    // auto-sauvegardées (#341) — sur le profil fraîchement créé. On réutilise le
    // helper mutualisé avec le panneau « Mes profils » (`applyResponse`).
    if (json.criterias) applyFiltersForm(json.criterias);

    // Résilience : refléter le profil créé dans l'URL (sans recharger) pour
    // qu'un rafraîchissement rouvre le simulateur sur ce profil, sans le wizard.
    this.syncUrlToProfile();
  }

  markProfileSecondary() {
    const section = document.querySelector("[data-profile-name]");
    if (section) section.setAttribute("data-profile-principal", "false");
  }

  pushLabel(label) {
    if (!this.editForm) return;
    setNamedField(this.editForm, "criterias[label]", label);
    this.dispatchFormSync();
  }

  // ---------- Application d'un écran aux champs canoniques ----------
  applyStep(stepElement) {
    if (!stepElement || !this.editForm) return;

    // Quand l'interrupteur « Primo-accédant » est OFF (no_dispositive_residence),
    // les champs occupants/RFR N-2 (`depends_on: primo`) sont masqués et exclus
    // de l'application (cf. #381).
    const primoOn = this.primoState(stepElement);
    const fields = this.fieldTargets.filter(
      (field) =>
        stepElement.contains(field) &&
        !(primoOn === false && field.dataset.fieldDependsOn === "primo")
    );
    fields.forEach((field) => this.applyField(field));

    // Le champ readonly « Salaires annuels nets du foyer » vaut 12 × les revenus
    // mensuels : le recalcul est branché sur l'`input` du champ mensuel, que ce
    // flux programmatique ne déclenche pas (#583).
    if (fields.some((field) => field.dataset.fieldName === "criterias[monthly_incomes]")) {
      syncHouseholdIncomes(this.editForm);
    }

    // Un seul déclenchement input/change après avoir poussé tous les champs de
    // l'écran : persistance (`edit-profile#debouncedSubmit`) + recalcul
    // (`api-sync`) côté pipelines existants.
    this.dispatchFormSync();
  }

  applyField(field) {
    const name = field.dataset.fieldName;
    const kind = field.dataset.fieldKind;
    if (!name) return;

    switch (kind) {
      case "mode":
        this.applyMode(this.selectedCardValue(field));
        break;
      case "fiscality":
        this.applyTaxProgram(this.selectedCardValue(field));
        break;
      case "primo_accedant":
        this.applyResidenceTaxProgram(this.primoValue(field));
        break;
      case "rooms":
        this.applyRooms(name, field);
        break;
      case "location":
        this.applyLocation(name, field);
        break;
      case "stepper":
        setNamedField(this.editForm, name, this.stepperValue(field));
        break;
      case "number":
        setNamedField(this.editForm, name, this.numberValue(field));
        break;
      default:
        setNamedField(this.editForm, name, this.fieldValue(field));
    }
  }

  // Bascule Résidence principale / Investissement (mêmes effets que la bascule
  // du formulaire : aligne `investment_mode`, force `tax_program` et réveille les
  // contrôleurs branchés sur le `change` du radio tax_program).
  applyMode(modeValue) {
    if (!modeValue || !this.editForm) return;

    const isResidence = this.modeMapping[modeValue] === "main_residence";
    const taxProgram = isResidence
      ? "ptz"
      : (this.currentInvestTaxProgram() || this.lastInvestTaxProgram || "no_dispositive_invest");

    this.editForm.querySelectorAll(`[name="${this.cssName(this.modeField)}"]`).forEach((radio) => {
      const checked = radio.value === modeValue;
      radio.checked = checked;
      const label = radio.closest("label");
      if (label) label.classList.toggle("active", checked);
    });

    try {
      this.editForm.dispatchEvent(
        new CustomEvent("toggle:set", { bubbles: true, detail: { scope: "tax_program", value: taxProgram } })
      );
    } catch (_) {}

    this.emitTaxProgramChange(taxProgram);
  }

  applyTaxProgram(value) {
    if (!value) return;
    this.lastInvestTaxProgram = value;
    this.emitTaxProgramChange(value);
  }

  // Fiscalité Résidence principale (interrupteur Primo-accédant) : `ptz` ou
  // `no_dispositive_residence`. Ne pollue PAS `lastInvestTaxProgram` (réservé aux
  // dispositifs d'investissement, utilisé par `applyMode`).
  applyResidenceTaxProgram(value) {
    if (!value) return;
    this.emitTaxProgramChange(value);
  }

  emitTaxProgramChange(taxProgram) {
    const selector = `input[name="${this.cssName("criterias[tax_program]")}"][value="${this.cssName(taxProgram)}"]`;
    const radio = this.editForm.querySelector(selector);
    if (!radio) return;
    radio.checked = true;
    try { radio.dispatchEvent(new Event("change", { bubbles: true })); } catch (_) {}
  }

  // ---------- Réactivité au clic (sélection visuelle immédiate) ----------
  // Carte radio (mode / fiscalité) : pose `.active` sur le label sélectionné et
  // la retire des autres du même groupe, dès le clic (le moteur ne togglait
  // `.active` qu'à la navigation/préremplissage).
  cardChanged(event) {
    const field = event.target.closest('[data-profile-wizard-target="field"]');
    if (!field) return;
    field.querySelectorAll("label").forEach((label) => {
      const radio = label.querySelector('input[type="radio"]');
      if (radio) label.classList.toggle("active", radio.checked);
    });
  }

  // Checkbox de typologie (rooms) : reflète l'état sélectionné sur le label dès
  // le clic.
  roomToggled(event) {
    const input = event.target;
    const label = input.closest("label");
    if (label) label.classList.toggle("active", input.checked);
  }

  // Interrupteur « Primo-accédant » (écran Résidence principale) : ON ⇒ `ptz` +
  // champs occupants/RFR N-2 visibles, OFF ⇒ `no_dispositive_residence` + champs
  // masqués. Applique la fiscalité immédiatement (recalcul live) (cf. #381).
  primoToggled(event) {
    const field = event.target.closest('[data-profile-wizard-target="field"]');
    const step = field ? field.closest('[data-profile-wizard-target="step"]') : null;
    const on = event.target.checked;
    this.applyPrimoVisibility(step, on);
    this.applyResidenceTaxProgram(on ? "ptz" : "no_dispositive_residence");
    this.dispatchFormSync();
  }

  // Masque/affiche les champs `depends_on: primo` de l'écran selon l'état de
  // l'interrupteur.
  applyPrimoVisibility(stepElement, on) {
    if (!stepElement) return;
    this.fieldTargets
      .filter((f) => stepElement.contains(f) && f.dataset.fieldDependsOn === "primo")
      .forEach((f) => f.classList.toggle("d-none", !on));
  }

  // État de l'interrupteur Primo-accédant de l'écran (true/false), ou null si
  // l'écran n'en porte pas (pas de gating).
  primoState(stepElement) {
    const field = this.fieldTargets.find(
      (f) => stepElement.contains(f) && f.dataset.fieldKind === "primo_accedant"
    );
    if (!field) return null;
    const input = field.querySelector('input[type="checkbox"]');
    return input ? input.checked : null;
  }

  primoValue(field) {
    const input = field.querySelector('input[type="checkbox"]');
    return input && input.checked ? "ptz" : "no_dispositive_residence";
  }

  applyRooms(name, field) {
    const selected = Array.from(field.querySelectorAll("input[type=checkbox]"))
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => String(checkbox.value));

    const canonical = Array.from(
      this.editForm.querySelectorAll(`input[type=checkbox][name="${this.cssName(name)}"]`)
    );
    canonical.forEach((checkbox) => {
      const checked = selected.includes(String(checkbox.value));
      checkbox.checked = checked;
      const label = checkbox.closest("label");
      if (label) label.classList.toggle("active", checked);
    });
  }

  // ---------- Localisation (autocomplétion Google Places, #382) ----------
  // Pousse le libellé textuel saisi dans `criterias[invest_location]` (`name`)
  // ET les valeurs renseignées par le contrôleur `places-autocomplete` sur ses
  // cibles cachées (région/département/lat/lng/cityId) vers leurs champs
  // canoniques respectifs, identifiés par `data-canonical`.
  applyLocation(name, field) {
    const input = field.querySelector('[data-places-autocomplete-target="input"]');
    setNamedField(this.editForm, name, input ? input.value : "");

    field.querySelectorAll("[data-canonical]").forEach((hidden) => {
      const canonical = hidden.dataset.canonical;
      if (canonical) setNamedField(this.editForm, canonical, hidden.value);
    });
  }

  // ---------- Compteur générique (stepper) ----------
  stepperIncrement(event) {
    this.stepperAdjust(event, 1);
  }

  stepperDecrement(event) {
    this.stepperAdjust(event, -1);
  }

  // Incrémente/décrémente la valeur du compteur de l'écran courant en
  // respectant min/max/step (paramétrés via les `data-stepper-*` du champ). La
  // valeur n'est poussée dans le champ canonique qu'au passage à l'écran suivant
  // (`applyStep`), comme les autres champs.
  stepperAdjust(event, direction) {
    if (event) event.preventDefault();
    const field = event.currentTarget.closest('[data-profile-wizard-target="field"]');
    if (!field) return;
    const step = parseFloat(field.dataset.stepperStep) || 1;
    this.setStepperValue(field, this.stepperValue(field) + direction * step);
  }

  stepperValue(field) {
    const raw = field.dataset.stepperCurrent;
    if (raw !== undefined && raw !== "") {
      const parsed = parseFloat(raw);
      if (!isNaN(parsed)) return parsed;
    }
    return this.stepperMin(field);
  }

  stepperMin(field) {
    const min = parseFloat(field.dataset.stepperMin);
    return isNaN(min) ? 0 : min;
  }

  setStepperValue(field, value) {
    let next = value;
    const min = this.stepperMin(field);
    if (next < min) next = min;
    const maxRaw = field.dataset.stepperMax;
    if (maxRaw !== undefined && maxRaw !== "") {
      const max = parseFloat(maxRaw);
      if (!isNaN(max) && next > max) next = max;
    }
    field.dataset.stepperCurrent = String(next);
    const number = field.querySelector(".simulator-wizard-stepper__number");
    if (number) number.textContent = String(next);
  }

  prefillStepper(field, value) {
    const parsed = value == null || value === "" ? this.stepperMin(field) : parseFloat(value);
    this.setStepperValue(field, isNaN(parsed) ? this.stepperMin(field) : parsed);
  }

  // ---------- Préremplissage depuis les champs canoniques ----------
  prefillFromCanonical() {
    if (!this.editForm) return;

    this.fieldTargets.forEach((field) => {
      const name = field.dataset.fieldName;
      const kind = field.dataset.fieldKind;
      if (!name) return;

      // L'écran 1 (nom du profil) conserve sa valeur par défaut « Profil N »
      // rendue côté serveur : pas de préremplissage depuis un champ canonique.
      if (kind === "profile_name") return;

      if (kind === "mode") {
        this.prefillMode(field);
      } else if (kind === "fiscality") {
        this.prefillCard(field, readNamedFieldValue(this.editForm, "criterias[tax_program]"));
      } else if (kind === "primo_accedant") {
        this.prefillPrimo(field);
      } else if (kind === "rooms") {
        this.prefillRooms(name, field);
      } else if (kind === "location") {
        this.prefillLocation(name, field);
      } else if (kind === "stepper") {
        this.prefillStepper(field, readNamedFieldValue(this.editForm, name));
      } else if (kind === "number") {
        this.prefillNumber(field, readNamedFieldValue(this.editForm, name));
      } else {
        this.prefillSimple(field, readNamedFieldValue(this.editForm, name));
      }
    });
  }

  prefillMode(field) {
    const current = readNamedFieldValue(this.editForm, this.modeField);
    this.prefillCard(field, current);
    const tax = readNamedFieldValue(this.editForm, "criterias[tax_program]");
    if (tax && tax !== "ptz") this.lastInvestTaxProgram = tax;
  }

  // Préremplit l'interrupteur Primo-accédant depuis la fiscalité canonique :
  // `no_dispositive_residence` ⇒ OFF, sinon ON (défaut). Applique aussi la
  // visibilité initiale des champs dépendants.
  prefillPrimo(field) {
    const current = readNamedFieldValue(this.editForm, "criterias[tax_program]");
    const on = current !== "no_dispositive_residence";
    const input = field.querySelector('input[type="checkbox"]');
    if (input) input.checked = on;
    const step = field.closest('[data-profile-wizard-target="step"]');
    this.applyPrimoVisibility(step, on);
  }

  prefillCard(field, value) {
    field.querySelectorAll("input[type=radio]").forEach((radio) => {
      const checked = String(radio.value) === String(value);
      radio.checked = checked;
      const label = radio.closest("label");
      if (label) label.classList.toggle("active", checked);
    });
  }

  prefillRooms(name, field) {
    const checkedValues = Array.from(
      this.editForm.querySelectorAll(`input[type=checkbox][name="${this.cssName(name)}"]`)
    )
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => String(checkbox.value));

    field.querySelectorAll("input[type=checkbox]").forEach((checkbox) => {
      const checked = checkedValues.includes(String(checkbox.value));
      checkbox.checked = checked;
      const label = checkbox.closest("label");
      if (label) label.classList.toggle("active", checked);
    });
  }

  // Réinjecte le libellé `invest_location` déjà saisi dans l'input visible et
  // les valeurs de géocodage déjà connues dans les cachés (#382).
  prefillLocation(name, field) {
    const input = field.querySelector('[data-places-autocomplete-target="input"]');
    if (input) {
      const value = readNamedFieldValue(this.editForm, name);
      input.value = value == null ? "" : String(value);
    }

    field.querySelectorAll("[data-canonical]").forEach((hidden) => {
      const canonical = hidden.dataset.canonical;
      if (!canonical) return;
      const value = readNamedFieldValue(this.editForm, canonical);
      hidden.value = value == null ? "" : String(value);
    });
  }

  prefillNumber(field, value) {
    const input = field.querySelector("input");
    if (!input) return;
    input.value = value == null ? "" : String(value).replace(".", ",");
    try { input.dispatchEvent(new Event("number-format:normalize", { bubbles: true })); } catch (_) {}
  }

  prefillSimple(field, value) {
    const input = field.querySelector("input, select, textarea");
    if (!input) return;
    input.value = value == null ? "" : String(value);
  }

  // ---------- Lecture des valeurs d'un champ de wizard ----------
  selectedCardValue(field) {
    const checked = Array.from(field.querySelectorAll("input[type=radio]")).find((r) => r.checked);
    return checked ? checked.value : null;
  }

  numberValue(field) {
    const input = field.querySelector("input");
    if (!input) return null;
    if (input.dataset && input.dataset.raw !== undefined && input.dataset.raw !== "") return input.dataset.raw;
    return input.value;
  }

  fieldValue(field) {
    const input = field.querySelector("input, select, textarea");
    return input ? input.value : null;
  }

  profileNameValue() {
    const input = this.element.querySelector('[data-field-kind="profile_name"] input');
    return input ? input.value : this.defaultLabelValue;
  }

  currentInvestTaxProgram() {
    const current = readNamedFieldValue(this.editForm, "criterias[tax_program]");
    return current && current !== "ptz" ? current : null;
  }

  // ---------- Synchronisation pipelines ----------
  dispatchFormSync() {
    if (!this.editForm) return;
    try { this.editForm.dispatchEvent(new Event("change", { bubbles: true })); } catch (_) {}
    try { this.editForm.dispatchEvent(new Event("input", { bubbles: true })); } catch (_) {}
  }

  // ---------- Fermeture ----------
  close() {
    this.element.classList.add("d-none");
    document.body.classList.remove("simulator-wizard-open");
    this.syncUrlToProfile();
  }

  syncUrlToProfile() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("new_profile");
      url.searchParams.delete("profile_wizard");
      if (this.profileId) url.searchParams.set("profile_id", String(this.profileId));
      window.history.replaceState({}, "", url.toString());
    } catch (_) {}
  }

  // ---------- Utilitaires ----------
  cssName(value) {
    if (typeof window !== "undefined" && window.CSS && typeof window.CSS.escape === "function") {
      return window.CSS.escape(value);
    }
    return String(value).replace(/["\\\]]/g, "\\$&");
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute("content") : "";
  }
}
